import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output } from '@angular/core';
import {
  AccountSelectedEvent,
  AddressMultiProductCards,
  AddressProducts,
  ILSystem,
  IMultiProductCard,
  NavigatingBackTo,
  Product,
  Sparte,
} from '@mwe/models';
import { LoadingService, productStore } from '@mwe/services';
import { createMultiProductCardsByAccountNumber, isArrayWithMinOneItem, isArrayWithOneItem, sortProductsByCategory } from '@mwe/utils';
import { toSignal } from '@angular/core/rxjs-interop';
import { PreLoadDataComponent } from '../../pre-load-data/pre-load-data.component';
import { NoAssociationsFoundWarningComponent } from '../../no-associations-found-warning/no-associations-found-warning.component';
import { AddressComponent } from '../../address/address.component';
import { BasicCardClickableComponent } from '../../cards/card-clickable/basic-card-clickable.component';
import { ProductIconsComponent } from '../../product-icons/product-icons.component';
import { TranslateDirective } from '@ngx-translate/core';
import { CorrelatedCompanyReferencePipe } from '@mwe/ui/pipes';

@Component({
  selector: 'mwe-account-selection',
  templateUrl: './account-selection.component.html',
  imports: [
    PreLoadDataComponent,
    NoAssociationsFoundWarningComponent,
    AddressComponent,
    BasicCardClickableComponent,
    ProductIconsComponent,
    TranslateDirective,
    CorrelatedCompanyReferencePipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountSelectionComponent {
  private readonly loadingService = inject(LoadingService);
  private readonly productStore = inject(productStore);

  readonly categoryWhitelist = input<Sparte[]>([]);
  readonly noAddressGroupsTitle = input<string>('');
  readonly noAddressGroupsText = input<string>('');
  readonly navigatingBackTo = input<NavigatingBackTo>();

  readonly accountSelected = output<AccountSelectedEvent>();
  readonly hasNoAddressProducts = output<void>();
  readonly selectionSkipped = output<void>();

  readonly state = toSignal(this.loadingService.getLatestMergedLoadingStates('addressProducts'));

  readonly hasNoAddress = computed<boolean>(() => !isArrayWithMinOneItem(this.addressOptions()));
  readonly addressOptions = computed<AddressProducts[]>(() => {
    const addressOptions = this.productStore.addressGroups().map(group => {
      return {
        ...group,
        products: group.products.filter(p => {
          if (!isArrayWithMinOneItem(this.categoryWhitelist())) {
            return true;
          }

          return this.categoryWhitelist().some(c => c.toLowerCase() === p.category.toLowerCase());
        }),
      };
    });
    return addressOptions.filter(ao => isArrayWithMinOneItem(ao.products));
  });
  readonly addressMultiProductCards = computed<AddressMultiProductCards[]>(() => {
    return this.addressOptions().map(ao => {
      const sortedProducts = sortProductsByCategory(ao.products, true);
      return {
        address: ao.address,
        multiProductCards: createMultiProductCardsByAccountNumber(sortedProducts as Product[]),
      };
    });
  });

  constructor() {
    effect(() => {
      const onlyOneAddress = isArrayWithOneItem(this.addressMultiProductCards());
      const onlyOneCard = isArrayWithOneItem(this.addressMultiProductCards()?.[0]?.multiProductCards);

      if (onlyOneAddress && onlyOneCard) {
        this.selectionSkipped.emit();
        this.onMultiCardClicked(this.addressMultiProductCards()[0], this.addressMultiProductCards()[0].multiProductCards[0]);
      }
    });
  }

  onMultiCardClicked(item: AddressMultiProductCards, card: IMultiProductCard): void {
    const addressProducts = this.addressOptions().filter(option => option.address?.hashCode === item.address?.hashCode);
    let filteredAddressProducts: AddressProducts;

    if (addressProducts?.length === 1) {
      filteredAddressProducts = addressProducts[0];
    } else {
      filteredAddressProducts = addressProducts.find(ap => ap?.address?.hint === item?.address?.hint);
    }

    filteredAddressProducts.products = filteredAddressProducts.products.filter(p => card.accountNumber === p.accountNumber);
    const systemId = filteredAddressProducts?.products?.[0]?.systemId as ILSystem;
    const accountId = filteredAddressProducts?.products?.[0]?.accountNumber;

    this.accountSelected.emit({ systemId, accountId, addressProducts: filteredAddressProducts });
  }
}
